import { isPlatformBrowser } from '@angular/common';
import type { AfterViewInit, OnDestroy } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  PLATFORM_ID,
} from '@angular/core';
import { ConnectionStatus, WebSocketService } from '@freelancer/datastore';
import { FreelancerBreakpointValues } from '@freelancer/ui/breakpoints';
import {
  ToastAlertColor,
  ToastAlertService,
  ToastAlertType,
} from '@freelancer/ui/toast-alert';
import type { Subscription } from 'rxjs';

@Component({
  selector: 'fl-network-alert',
  template: ``,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NetworkAlertComponent implements AfterViewInit, OnDestroy {
  private websocketDisconnectedSubscription?: Subscription;
  private previousStatus?: ConnectionStatus;

  constructor(
    private websocket: WebSocketService,
    private toastAlertService: ToastAlertService,
    @Inject(PLATFORM_ID) private platformId: string,
  ) {}

  ngAfterViewInit(): void {
    if (isPlatformBrowser(this.platformId) && !this.isMobileViewport()) {
      this.websocketDisconnectedSubscription = this.websocket
        .getConnectStatusForUser()
        .subscribe(detail => {
          if (detail.status === ConnectionStatus.CLOSED) {
            this.toastAlertService.open({
              id: 'network-alert-offline',
              type: ToastAlertType.ERROR,
              color: ToastAlertColor.DARK,
              message: $localize`Unable to connect to chat, retrying in ${detail.retryDelayInSeconds}s`,
              closeable: true,
              indefinite: true,
              reopenIfExists: true,
            });
          } else if (this.previousStatus === ConnectionStatus.CLOSED) {
            this.toastAlertService.close('network-alert-offline');

            this.toastAlertService.open({
              id: 'network-alert-online',
              type: ToastAlertType.SUCCESS, // Success type for online notification
              color: ToastAlertColor.LIGHT,
              message: $localize`You are back online`,
              closeable: true,
              reopenIfExists: true,
            });
          }

          // Update previous status for next comparison
          this.previousStatus = detail.status;
        });
    }
  }

  ngOnDestroy(): void {
    this.websocketDisconnectedSubscription?.unsubscribe();
  }

  private isMobileViewport(): boolean {
    return window.innerWidth < parseInt(FreelancerBreakpointValues.TABLET, 10);
  }
}
